/**
 * PX-1 — durable pre-auth intent.
 *
 * A visitor's original intent (which side of the marketplace they are on, the
 * freight they described on the homepage, where they were heading, which
 * referral brought them) must survive signup, email confirmation and
 * onboarding. Previously the homepage put that intent in the signup URL and
 * signup read only `next`, so every high-intent visitor re-typed their load.
 *
 * This store is CONVENIENCE ONLY. It is browser-owned, never authoritative:
 * it cannot grant a role, a capability, a price or a location. Role provision
 * still runs through complete_profile_onboarding / provision_identity, and a
 * restored pickup/delivery is only seed TEXT — the user must re-select a real
 * place so route and pricing stay authoritative.
 */
import { roleHome, type AppRole } from "@/lib/roles";

const STORAGE_KEY = "ml_preauth_intent_v1";

export type IntentRole = Extract<AppRole, "shipper" | "transporter" | "driver">;
const INTENT_ROLES: IntentRole[] = ["shipper", "transporter", "driver"];

export interface QuoteIntent {
  pickup: string;
  delivery: string;
  goods: string;
}

export interface PreAuthIntent {
  role?: IntentRole;
  /** Same-origin path to resume after auth. */
  next?: string;
  quote?: QuoteIntent;
  /** Referral / invite code (consumed server-side in PX-8). */
  ref?: string;
  /** Which CTA captured the intent — telemetry only. */
  source?: string;
  captured_at?: string;
}

/** Only same-origin absolute paths may be resumed. */
export function safePath(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (!value.startsWith("/") || value.startsWith("//")) return undefined;
  return value;
}

function normaliseRole(value: string | null | undefined): IntentRole | undefined {
  return INTENT_ROLES.includes(value as IntentRole) ? (value as IntentRole) : undefined;
}

function cleanQuote(q: Partial<QuoteIntent> | undefined): QuoteIntent | undefined {
  if (!q) return undefined;
  const pickup = (q.pickup ?? "").trim().slice(0, 200);
  const delivery = (q.delivery ?? "").trim().slice(0, 200);
  const goods = (q.goods ?? "").trim().slice(0, 200);
  if (!pickup && !delivery && !goods) return undefined;
  return { pickup, delivery, goods };
}

export function hasQuoteIntent(intent: PreAuthIntent | null | undefined): boolean {
  return Boolean(intent?.quote && (intent.quote.pickup || intent.quote.delivery || intent.quote.goods));
}

/** Parse intent carried on a URL (?role=&pickup=&delivery=&goods=&next=&ref=). */
export function intentFromSearch(sp: URLSearchParams): PreAuthIntent {
  return {
    role: normaliseRole(sp.get("role")),
    next: safePath(sp.get("next")),
    quote: cleanQuote({
      pickup: sp.get("pickup") ?? "",
      delivery: sp.get("delivery") ?? "",
      goods: sp.get("goods") ?? "",
    }),
    ref: (sp.get("ref") ?? "").trim().slice(0, 64) || undefined,
    source: (sp.get("src") ?? "").trim().slice(0, 40) || undefined,
  };
}

/** URL-carried intent wins over stored intent, field by field. */
export function mergeIntent(
  primary: PreAuthIntent | null | undefined,
  fallback: PreAuthIntent | null | undefined,
): PreAuthIntent {
  return {
    role: primary?.role ?? fallback?.role,
    next: primary?.next ?? fallback?.next,
    quote: primary?.quote ?? fallback?.quote,
    ref: primary?.ref ?? fallback?.ref,
    source: primary?.source ?? fallback?.source,
    captured_at: primary?.captured_at ?? fallback?.captured_at,
  };
}

export function readStoredIntent(): PreAuthIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PreAuthIntent;
    return {
      role: normaliseRole(parsed.role),
      next: safePath(parsed.next),
      quote: cleanQuote(parsed.quote),
      ref: parsed.ref,
      source: parsed.source,
      captured_at: parsed.captured_at,
    };
  } catch {
    return null;
  }
}

/** Persist intent at the CTA, merging over anything already captured. */
export function captureIntent(partial: PreAuthIntent): PreAuthIntent {
  const merged = mergeIntent(
    { ...partial, quote: cleanQuote(partial.quote), next: safePath(partial.next), role: normaliseRole(partial.role) },
    readStoredIntent(),
  );
  merged.captured_at = new Date().toISOString();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* private mode — intent degrades to the URL only */
  }
  return merged;
}

export function clearIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Read the effective intent for the current URL (URL first, store second). */
export function resolveIntent(sp: URLSearchParams): PreAuthIntent {
  return mergeIntent(intentFromSearch(sp), readStoredIntent());
}

/** Search string that carries intent onto the next auth step. */
export function intentSearch(intent: PreAuthIntent): string {
  const sp = new URLSearchParams();
  if (intent.role) sp.set("role", intent.role);
  if (intent.next) sp.set("next", intent.next);
  if (intent.ref) sp.set("ref", intent.ref);
  if (intent.source) sp.set("src", intent.source);
  if (intent.quote?.pickup) sp.set("pickup", intent.quote.pickup);
  if (intent.quote?.delivery) sp.set("delivery", intent.quote.delivery);
  if (intent.quote?.goods) sp.set("goods", intent.quote.goods);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function withIntent(path: string, intent: PreAuthIntent): string {
  return `${path}${intentSearch(intent)}`;
}

/**
 * Where an authenticated user with this intent should land.
 * An explicit `next` wins; a shipper carrying a homepage load resumes booking;
 * otherwise the role's normal home. Never returns a protected action route —
 * the destination is still authorised by RequireAuth and central policy.
 */
export function resumeTarget(role: AppRole | null | undefined, intent: PreAuthIntent): string {
  if (intent.next) return intent.next;
  if (role === "shipper" && hasQuoteIntent(intent)) return "/app/shipper/create?resume=1";
  return roleHome(role ?? undefined);
}

/**
 * Take the homepage quote once, for the booking wizard. Returns seed text only
 * and clears the quote so a resumed draft is never replayed twice.
 */
export function consumeQuoteIntent(): QuoteIntent | null {
  const stored = readStoredIntent();
  if (!stored || !hasQuoteIntent(stored)) return null;
  const { quote, ...rest } = stored;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  } catch {
    /* ignore */
  }
  return quote ?? null;
}
